import { useRef, useEffect } from 'react';
import ModalOverlay from './ModalOverlay';
import { useSpecificationContext } from '@context/Specification';

const SpecificationsModalOverlay = ({ car }) => {
    const { setCarId } = useSpecificationContext();
    const modalRef = useRef(null);
    const locale = 'nl-NL';

    /**
     * Open the modal after the first render.
     */
    useEffect(() => {
        modalRef.current.open = true;
    }, []);

    /**
     * Listen for the close event on the modal overlay.
     * Whenever it's closed, wait for the fade-out transition to complete
     * before destroying the component.
     */
    useEffect(() => {
        modalRef.current.addEventListener('modaloverlaychange', event => {
            if (event.detail.isOpen === false) {
                event.target.addEventListener(
                    'transitionend',
                    () => {
                        setCarId(null);
                    },
                    { once: true }
                );
            }
        });
    }, [setCarId]);

    return (
        <ModalOverlay ref={modalRef}>
            <div>
                <h2 className="title mb-3">
                    <span>
                        {car.brand} {car.model}
                    </span>
                    <small>
                        {car.transmissionName} {car.type}
                    </small>
                </h2>

                <h3 className="subtitle">Algemeen</h3>
                <table className="table mb-5">
                    <tbody>
                        <tr>
                            <th>Merk</th>
                            <td>{car.brand}</td>
                        </tr>
                        <tr>
                            <th>Model</th>
                            <td>{car.model}</td>
                        </tr>
                        <tr>
                            <th>Uitvoering</th>
                            <td>
                                {car.type.includes('- ')
                                    ? car.type.substring(2)
                                    : car.type}
                            </td>
                        </tr>
                        <tr>
                            <th>Jaar</th>
                            <td>{car.year}</td>
                        </tr>
                        <tr>
                            <th>Kilometerstand</th>
                            <td>
                                {car.mileage.toLocaleString(locale)} kilometer
                            </td>
                        </tr>
                        {car.formattedLicensePlate ? (
                            <tr>
                                <th>Kenteken</th>
                                <td>{car.formattedLicensePlate}</td>
                            </tr>
                        ) : (
                            <tr>
                                <th>Kenteken</th>
                                <td>{car.licensePlate}</td>
                            </tr>
                        )}
                        {car.colorDefaultName && (
                            <tr>
                                <th>Kleur Exterieur</th>
                                <td>{car.colorDefaultName}</td>
                            </tr>
                        )}
                        {car.colorInterior && (
                            <tr>
                                <th>Kleur Interieur</th>
                                <td>{car.colorInterior}</td>
                            </tr>
                        )}
                        {car.bodyName && (
                            <tr>
                                <th>Carrosserie</th>
                                <td>{car.bodyName}</td>
                            </tr>
                        )}
                        {car.doors && (
                            <tr>
                                <th>Aantal deuren</th>
                                <td>{car.doors}</td>
                            </tr>
                        )}
                        {car.seats && (
                            <tr>
                                <th>Aantal zitplaatsen</th>
                                <td>{car.seats}</td>
                            </tr>
                        )}
                        {car.fuelTypeName && (
                            <tr>
                                <th>Brandstof</th>
                                <td>{car.fuelTypeName}</td>
                            </tr>
                        )}
                        {car.transmissionName && (
                            <tr>
                                <th>Schakeling</th>
                                <td>{car.transmissionName}</td>
                            </tr>
                        )}
                        {car.gears && (
                            <tr>
                                <th>Aantal versnellingen</th>
                                <td>{car.gears}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className="subtitle">Technische specificaties</h3>
                <table className="table mb-3">
                    <tbody>
                        {car.specifications?.powerPk && (
                            <tr>
                                <th>Vermogen</th>
                                <td>{car.specifications?.powerPk} pk</td>
                            </tr>
                        )}
                        {car.specifications?.topspeed && (
                            <tr>
                                <th>Topsnelheid</th>
                                <td>
                                    {car.specifications.topspeed} kilometer per
                                    uur
                                </td>
                            </tr>
                        )}
                        {car.specifications?.engineCapacity && (
                            <tr>
                                <th>Motorinhoud</th>
                                <td>{car.specifications.engineCapacity} cc</td>
                            </tr>
                        )}
                        {car.specifications?.numberOfCilinders && (
                            <tr>
                                <th>Aantal cilinders</th>
                                <td>{car.specifications.numberOfCilinders}</td>
                            </tr>
                        )}
                        {car.specifications?.driveTrainName && (
                            <tr>
                                <th>Aandrijving</th>
                                <td>{car.specifications.driveTrainName}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className="subtitle">Milieugegevens</h3>
                <table className="table mb-3">
                    <tbody>
                        {car.specifications.energyLabel && (
                            <tr>
                                <th>Energielabel</th>
                                <td>{car.specifications.energyLabel}</td>
                            </tr>
                        )}
                        {car.specifications.consumptionMixed && (
                            <tr>
                                <th>Gemiddeld verbruik</th>
                                <td>
                                    {car.specifications.consumptionMixed} liter
                                    per 100 km
                                </td>
                            </tr>
                        )}
                        {car.specifications.consumptionHighway && (
                            <tr>
                                <th>Verbruik stad</th>
                                <td>
                                    {car.specifications.consumptionHighway}{' '}
                                    liter per 100 km
                                </td>
                            </tr>
                        )}
                        {car.specifications.carbonOutput && (
                            <tr>
                                <th>
                                    CO<sub>2</sub>-uitstoot
                                </th>
                                <td>
                                    {car.specifications.carbonOutput} gram per 1
                                    km
                                </td>
                            </tr>
                        )}
                        {car.specifications.fuelCapacity && (
                            <tr>
                                <th>Tankinhoud</th>
                                <td>{car.specifications.fuelCapacity} L</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className="subtitle">Meten en gewicht</h3>
                <table className="table mb-3">
                    <tbody>
                        {car.measurements?.weight && (
                            <tr>
                                <th>Rijklaargewicht</th>
                                <td>
                                    {car.measurements.weight.toLocaleString(
                                        locale
                                    )}{' '}
                                    kilogram
                                </td>
                            </tr>
                        )}
                        {car.measurements?.maximumPermissibleMass && (
                            <tr>
                                <th>Maximale toelaatbare massa</th>
                                <td>
                                    {car.measurements.maximumPermissibleMass.toLocaleString(
                                        locale
                                    )}{' '}
                                    kilogram
                                </td>
                            </tr>
                        )}
                        {car.measurements?.trunkCapacity && (
                            <tr>
                                <th>Laadruimte</th>
                                <td>{car.measurements?.trunkCapacity} liter</td>
                            </tr>
                        )}
                        {car.measurements?.length && (
                            <tr>
                                <th>Lengte</th>
                                <td>
                                    {(
                                        car.measurements.length / 100
                                    ).toLocaleString(locale)}{' '}
                                    meter
                                </td>
                            </tr>
                        )}
                        {car.measurements?.width && (
                            <tr>
                                <th>Breedte</th>
                                <td>
                                    {(
                                        car.measurements.width / 100
                                    ).toLocaleString(locale)}{' '}
                                    meter
                                </td>
                            </tr>
                        )}
                        {car.measurements?.wheelbase && (
                            <tr>
                                <th>Wielbasis</th>
                                <td>
                                    {(
                                        car.measurements.wheelbase / 100
                                    ).toLocaleString(locale)}{' '}
                                    meter
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className="subtitle">Uitrusting</h3>
                {car.accessories.map((accessory, index) => (
                    <div key={`accessory-${index}`} className="mb-3">
                        <p>
                            <strong>{accessory.name}</strong>
                        </p>

                        <ul className="row list">
                            {accessory.items.map((item, index) => (
                                <li
                                    key={`accessory-item-${index}`}
                                    className="col-md-4 mb-2 text-gray7"
                                >
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </ModalOverlay>
    );
};

export default SpecificationsModalOverlay;
